import classNames from 'classnames';
import React from 'react';
import { Collapse } from 'tdesign-react';
import CollapsePanel from 'tdesign-react/es/collapse/CollapsePanel';
import FormItem from 'tdesign-react/es/form/FormItem';

import { useTypedSelector } from '@/store';

import { labelDom, LabelDomProps } from '../Label';
import type { IFormItemProps, IFormValue, IRenderType } from './interface';
import { renderFunMap } from './materials/render';

const RegistrationForm: React.FC<{
  form?: any;
  pageId?: string;
  className?: string;
  fieldsConfig: IFormItemProps[];
  repeatConfig?: {
    [key: string]: IFormItemProps[]
  };
  initFormData?: IFormValue;
  formData?: IFormValue;
  customRender?: { [key: string]: IRenderType };
  readonly?: boolean;
  theme?: 'show' | 'edit';
  labelConfig?: any;
  onChange?: (prop: string, value: any, opt?: any) => void;
}> = (props) => {
  const { fieldsConfig, labelConfig = {}, customRender = {}, readonly = false, initFormData, theme = 'edit', repeatConfig } = props;
  const tipList = useTypedSelector(store => store.tip);

  const getRenderFun = (render?: string | Function): any => {
    let renderFun: Function = renderFunMap.input;
    if (typeof render === 'string') {
      renderFun = renderFunMap[render] || customRender[render];
    }
    if (typeof render === 'function') {
      renderFun = render;
    }
    if (!render || !renderFun) {
      renderFun = renderFunMap.input;
    }
    return renderFun;
  };

  const getDisabledDom = (field: IFormItemProps, extraConfig: any) => {
    let defaultData = props.form.getFieldValue(field.props);
    if (typeof defaultData === 'undefined') {
      defaultData = initFormData?.[field.props || ''];
    }

    const renderName = theme === 'show' ? field.showRender : field.showRender || field.render || 'input';
    if (renderName) {
      const RenderFun = getRenderFun(renderName);
      return (
        <RenderFun
          placeholder=" "
          {...field.uiOptions}
          {...extraConfig}
          record={initFormData}
          disabled={true}
          cparams={field.cparams}
          props={field.props}
          form={props.form}
          fieldname={field.props}
        />
      );
    }
    if (Array.isArray(defaultData)) {
      return <>{defaultData.join(';')}</>;
    }
    return <>{defaultData}</>;
  };

  const getSpecialConfig = (field: IFormItemProps) => {
    if (
      field.render === 'radioSingle'
      && (initFormData?.[field.props || ''] === true || initFormData?.[field.props || ''] === field.uiOptions.value)
    ) {
      return { defaultChecked: true };
    }
  };

  /**
   * 表单只读，readonly = true
   * 表单不只读，readonly = false 字段有值时只读，field.readonly，初始化时，只读field.disabled
   * @returns
   */
  const getFormDom = (field: IFormItemProps) => {
    const repeatRenderArr = ['object', 'array'];
    const RenderFun = getRenderFun(field.render);
    const disabled = readonly
      || (!readonly && field.readonly && props.formData?.[field.props])
      || (props.formData && field.uiOptions?.disabled)
      || (field.readonly && field.uiOptions?.disabled);
    const extraConfig = repeatRenderArr.includes(field.render) ? { repeatConfig } : {};
    const config = getSpecialConfig(field);
    let tempData = {};
    if (field?.useFormData) {
      tempData = {
        formData: props.formData?.[field.props],
      };
    }
    return disabled ? (
      getDisabledDom(field, extraConfig)
    ) : (
      <RenderFun
        {...config}
        {...field.uiOptions}
        {...extraConfig}
        form={props.form}
        cparams={field.cparams}
        {...tempData}
        onChange={(val: any) => {
          props.onChange?.(field.props, val);
        }}
        fieldname={field.props}
      />
    );
  };

  const isSameValue = (value: any, data: any) => {
    // value dependValue, string | array
    // data 实际值，string | array
    if (!Array.isArray(data)) {
      return Array.isArray(value) ? value.includes(data) : value === data;
    }
    // data 数组结构 - 多选情况
    if (!Array.isArray(value)) {
      return data.includes(value);
    }
    // 都是数组取交集
    return value.length + data.length !== new Set([...value, ...data]).size;
  };

  const isNoInShow = (field: any, data: any) => field.type === 'show' && !isSameValue(field.value, data);

  const isHidden = (field: any, data: any) => field.type === 'hidden' && isSameValue(field.value, data);

  const isDisabledField = (field: any, data: any) => field.type === 'disabled' && isSameValue(field.value, data);

  const getDependValue = (key: string) => {
    let value = props.form.getFieldValue(key);
    if (typeof value === 'undefined') {
      value = initFormData?.[key];
    }
    return value;
  };

  const getSingleDom = (field: IFormItemProps, keyIndex: number) => {
    const tmpField = field;
    if (field.uiOptions?.defaultOptions && field.uiOptions?.depend) {
      const value = getDependValue(field.uiOptions.depend);
      if (value) {
        tmpField.uiOptions.options = field.uiOptions.defaultOptions.filter((item: any) => item.depend === value);
      } else {
        tmpField.uiOptions.options = [];
      }
    }
    if (field.dependFields) {
      let flag = true;
      field.dependFields?.forEach((item: any) => {
        const value = getDependValue(item.key);
        if (isDisabledField(item, value)) {
          tmpField.uiOptions.disabled = true;
        }
        if (isNoInShow(item, value) || isHidden(item, value)) {
          flag = false;
        }
      });
      if (!flag) {
        return null;
      }
    }

    const { labelName } = labelConfig;
    const labelProps: LabelDomProps = {
      field,
      initFormData,
      pageId: props.pageId,
      tipList,
    };

    return (
      field.hasCollapse
        ? <Collapse
          key={field.props || keyIndex}
          className={`formProps-${field.className ? field.className : field.props}`}
          borderless
          expandIconPlacement='right'
          defaultExpandAll
        >
          <CollapsePanel
            header={labelDom(labelProps)}
          className={classNames('base-form-item', 'custom-collapse-panel')}
        >
          <FormItem
              key={field.props || keyIndex}
            name={typeof labelName === 'undefined' ? field.props : [labelName, field.props]}
            rules={field.rules}
            {...labelConfig}
            {...field.labelConfig}
            requiredMark={false}
            initialData={typeof initFormData?.[field.props] !== 'undefined' ? initFormData?.[field.props] : undefined}
            shouldUpdate={true}
          >
            {getFormDom(field)}
          </FormItem>
          </CollapsePanel>
        </Collapse>
        : <FormItem
          className={classNames(`formProps-${field.className ? field.className : field.props}`, 'base-form-item')}
          key={field.props || keyIndex}
          label={labelDom(labelProps)}
          name={typeof labelName === 'undefined' ? field.props : [labelName, field.props]}
          rules={field.rules}
          {...labelConfig}
          {...field.labelConfig}
          requiredMark={false}
          initialData={typeof initFormData?.[field.props] !== 'undefined' ? initFormData?.[field.props] : undefined}
          shouldUpdate={true}
        >
        {getFormDom(field)}
      </FormItem>
    );
  };

  if (readonly && !initFormData) {
    return <>数据加载中</>;
  }

  const filterConfig = () => {
    const configs = fieldsConfig.filter(item => item.render !== 'hidden');
    const resultConfigs: (IFormItemProps)[] = [];
    configs.forEach((e) => {
      if (e.render === 'repeat' && repeatConfig && e.replace) {
        const replaceConfig = { ...repeatConfig[e.replace] as any };
        Object.keys(replaceConfig).forEach((k) => {
          resultConfigs.push(replaceConfig[k]);
        });
        return;
      }
      resultConfigs.push(e);
    });
    return resultConfigs;
  };

  const resultConfigs = filterConfig();

  return <>{resultConfigs.map((item, keyIndex) => getSingleDom(item, keyIndex))}</>;
};

export default RegistrationForm;
